/** Mixin for Member Details / Profile  */
import _, { stubFalse } from 'lodash';
import {
  errormsg, countries, genders, states, nationals
} from '@/utils/constants';
import { clientNationalIds } from '@/utils/client-rules';

import {
  checkEmail, checkMobile, parseBirthdate, checkName
} from '@/utils/form-helpers';

import { parseGoogleAddress, calculateMemberAge, checkValidMobile } from '@/utils/helpers';

const defaultRequiredRule = { required: true, trigger: 'blur', message: errormsg.required };

export default {
  data() {
    return {
      member: {
        _id: undefined,
        profile: {
          firstName: undefined,
          middleName: undefined,
          lastName: undefined,
          preferredName: undefined,
          dob: undefined,
          gender: undefined,
          email: undefined,
          mobile: undefined,
          nationality: undefined,
          address: {
            formatted: undefined,
            addressLine1: undefined,
            addressLine2: undefined,
            suburb: undefined,
            state: undefined,
            postcode: undefined,
            country: undefined,
            isManual: false,
          },
          emergency: {
            name: undefined,
            mobile: undefined,
            relationship: undefined,
          },
          meta: {
            parentOrGuardian1: {
              number: undefined,
              email: undefined,
              mobile: undefined,
              phone: undefined,
            },
            wwc: {
              number: undefined,
              expiry: undefined,
              issuer: undefined
            }
          }
        },
      },
      rules: {
        'profile.firstName': defaultRequiredRule,
        'profile.lastName': defaultRequiredRule,
        'profile.email': [{ validator: checkEmail, trigger: 'blur' }],
        'profile.mobile': { validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode), trigger: 'blur' },
        'profile.preferredName': {
          required: stubFalse,
          trigger: ['change', 'blur'],
          validator: (rule, value, callback) => {
            if ((value && !_.isString(value)) || (value && !/^[a-zA-Z-.' ]*$/.test(value))) return callback(new Error('Invalid Name'));
            return callback();
          }
        },
        'profile.address': defaultRequiredRule,
        'profile.address.formatted': {
          trigger: ['change', 'blur'],
          validator: (rule, value, callback) => {
            if (!value) {
              const { profile } = this.member;
              const {
                addressLine1, suburb, state, postcode, country, isManual
              } = profile.address || {};
              if (!isManual) return callback(new Error('Field is required'));
              if (!addressLine1 || !suburb || !state || !postcode || !country) return callback(new Error('Field is required'));
              return callback();
            }
            return callback();
          }
        },
        'profile.emergency.name': {
          required: true,
          trigger: ['change', 'blur'],
          validator: (rule, value, callback) => {
            if (!value) return callback(new Error('Field is required'));
            if ((value && !_.isString(value)) || (value && !/^[a-zA-Z-.' ]*$/.test(value))) return callback(new Error('Invalid Name'));
            return callback();
          }
        },
        'profile.nationality': { required: true, trigger: 'change', message: errormsg.required },
        'profile.emergency.mobile': {
          trigger: 'blur',
          validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
        },
        'profile.meta.parentOrGuardian1.name': {
          required: true, trigger: 'blur', message: errormsg.required
        },
        'profile.meta.parentOrGuardian1.email': {
          required: true,
          trigger: 'blur',
          validator: (rule, value, callback) => {
            if (!value) callback(new Error('Email is required'));
            const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            if (value && re.test(String(value).toLowerCase())) return callback();
            return callback(new Error('Invalid email address'));
          }
        },
        'profile.meta.parentOrGuardian1.mobile': {
          required: true,
          trigger: 'blur',
          validator: checkMobile
        },
        'profile.meta.parentOrGuardian1.phone': {
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (value === undefined || value === '') return callback();
            const re = /^(?:\+?61|0)4[0-9]{8}$/;
            if (value && re.test(String(value).toLowerCase())) return callback();
            return callback(new Error('Invalid phone'));
          }
        },
        'profile.meta.wwc.number': {
          required: false, trigger: 'blur', message: errormsg.required
        },
        ...this.getCustomRules()
      },
      genders,
      countries,
      states,
    };
  },
  methods: {
    getCustomRules() {
      const currentNational = _.includes(clientNationalIds, +process.env.VUE_APP_NATIONAL_ID) ? nationals[process.env.VUE_APP_NATIONAL_ID] : 'default';
      const test = this.getRules(currentNational);
      return test;
    },
    getAddressData(addressData, place) {
      this.member.profile.address = parseGoogleAddress(addressData, place);
    },
    checkAddress(val, ref = '-MEMBERID') {
      if (!val) this.member.profile.address = val;

      this.$refs[`address${ref}`].focus();
      this.$refs[`form${ref}`].validateField('profile.address.formatted');
    },
    searchCountry(query, cb) {
      if (query && query !== '') {
        setTimeout(() => {
          cb(this.countries.filter((item) => item.label.toLowerCase()
            .indexOf(query.toLowerCase()) > -1));
        }, 200);
      } else {
        cb([]);
      }
    },
    checkNationality(ref = '-MEMBERID') {
      this.$refs[`form${ref}`].validateField('profile.nationality');
    },
    validateAddress(ref = '-MEMBERID') {
      this.$refs[`form${ref}`].validateField('profile.address.formatted');
    },
    getRules(currentNational) {
      switch (currentNational) {
        case 'nswisa':
          return {
            ...this.rules,
            // Custom rules for NSWISA
            'meta.homeClub': defaultRequiredRule,
            'meta.clubs': {
              required: false, trigger: 'blur', message: errormsg.required
            },
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18, trigger: ['change', 'blur'], validator: checkName
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
          };
        case 'dsa':
          return {
            ...this.rules,
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18, trigger: ['change', 'blur'], validator: checkName
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
          };
        case 'broome':
          return {
            ...this.rules,
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18, trigger: ['change', 'blur'], validator: checkName
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
          };
        case 'kanga':
          return {
            ...this.rules,
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18, trigger: ['change', 'blur'], validator: checkName
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
          };
        case 'fencing':
        case 'fencingnsw':
          return {
            ...this.rules,
            'profile.preferredFirstName': defaultRequiredRule,
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Name is required'));
                  return callback();
                }
                return callback();
              }
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
            // Custom rules for FENCING
            'meta.homeClub': {
              required: true,
              trigger: ['blur', 'change'],
              validator: (rule, value, callback) => {
                if (!value) return callback(new Error('Field is required'));
                return callback();
              }
            },
            'meta.weapon': {
              required: false, trigger: 'blur', message: errormsg.required
            },
            'meta.handDominance': {
              required: false, trigger: 'blur', message: errormsg.required
            },
            'meta.vaccinated': {
              required: false, trigger: 'blur', message: errormsg.required
            },
            'meta.vaccinationShot': {
              required: false, trigger: 'blur', message: errormsg.required
            },
          };

        case 'unisyd':
          return {
            ...this.rules,
            'meta.medical.hasMedical': defaultRequiredRule,
            'meta.medical.notes': defaultRequiredRule,
            'meta.photographyOptOut': defaultRequiredRule,
            'meta.isStaff': defaultRequiredRule,
            'meta.isStudent': defaultRequiredRule,
            'meta.findAboutUs': defaultRequiredRule,
            'profile.nationality': { required: false },
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18, trigger: ['change', 'blur'], validator: checkName
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
          };
        case 'gridiron':
          return {
            ...this.rules,
            'profile.meta.parentOrGuardian1.name': {
              required: false
            },
            'profile.meta.parentOrGuardian1.email': {
              required: false
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: false
            },
            'meta.nationalId': { required: false },
            'meta.additionalEmails': { required: false },
            'meta.isAboriginal': { required: true },
            'meta.hasAustralianPassport': { required: true },
            'meta.religion': { required: false },
            'meta.occupation': { required: false },
            'meta.householdIncome': { required: false },
            'meta.currentSports': { required: false },
            'meta.previousSports': { required: false },
          };
        case 'archery':
          return {
            ...this.rules,
            'profile.meta.parentOrGuardian1.name': {
              required: false
            },
            'profile.meta.parentOrGuardian1.email': {
              required: false
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: false
            },
            'meta.memberNumber': { required: false },
            'meta.homeClub': {
              required: true,
              trigger: ['blur', 'change'],
              validator: (rule, value, callback) => {
                if (!value) return callback(new Error('Field is required'));
                return callback();
              }
            },
            'meta.bowType': { required: false },
          };
        case 'bonduni':
          return {
            ...this.rules,
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18, trigger: ['change', 'blur'], validator: checkName
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
            'meta.bonduniMemberType': {
              required: true,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) return callback(new Error('Bond Uni member type is required'));
                return callback();
              }
            },
            'meta.bonduniId': {
              required: true,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) return callback(new Error('Bond Uni ID is required'));
                return callback();
              }
            },
          };
        case 'auckland':
          return {
            ...this.rules,
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18, trigger: ['change', 'blur'], validator: checkName
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
          };
        default:
          return {
            ...this.rules,
            'profile.meta.parentOrGuardian1.name': {
              required: this.isUnder18, trigger: ['change', 'blur'], validator: checkName
            },
            'profile.meta.parentOrGuardian1.email': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  if (this.isUnder18) return callback(new Error('Email is required'));
                  return callback();
                }
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (value && re.test(String(value).toLowerCase())) return callback();
                return callback(new Error('Invalid email address'));
              }
            },
            'profile.meta.parentOrGuardian1.mobile': {
              required: this.isUnder18,
              trigger: 'blur',
              validator: (rule, value, callback) => checkValidMobile(rule, value, callback, this.isUnder18, this.countryCode)
            },
          };
      }
    }
  },
  filters: {
    formatDob(val) {
      return parseBirthdate(val, true);
    },
  },
  computed: {
    isUnder18() {
      const endOfYear = this.$store.getters['national/endOfYear'];
      return calculateMemberAge(this.member.profile.dob, endOfYear) < 18;
    },
    currentNational() {
      if (_.includes(clientNationalIds, +process.env.VUE_APP_NATIONAL_ID)) return nationals[process.env.VUE_APP_NATIONAL_ID];
      return 'default';
    },
    nswisaData() {
      return {
        ...this.member,
        // Custom data for NSWISA
        meta: {
          proofOfAge: undefined,
          coachesEmails: [],
          homeClub: undefined,
          clubs: []
        }
      };
    },
    nswisaRules() {
      this.getRules(this.currentNational);
    },
    dsaData() {
      return {
        ...this.member,
        meta: {
          classification: undefined,
        }
      };
    },
    dsaRules() {
      this.getRules(this.currentNational);
    },
    broomeData() {
      return {
        ...this.member,
      };
    },
    broomeRules() {
      this.getRules(this.currentNational);
    },
    kangaData() {
      return {
        ...this.member,
      };
    },
    kangaRules() {
      this.getRules(this.currentNational);
    },
    fencingData() {
      return {
        ...this.member,
        // Custom data for Fencing
        meta: {
          weapon: undefined,
          handDominance: undefined,
          homeClub: undefined,
          vaccinated: false,
          vaccinationShot: undefined,
        }
      };
    },
    fencingRules() {
      this.getRules(this.currentNational);
    },
    fencingnswData() {
      return {
        ...this.member,
        // Custom data for Fencing
        meta: {
          weapon: undefined,
          handDominance: undefined,
          homeClub: undefined,
          vaccinated: false,
          vaccinationShot: undefined,
          school: undefined,
          volunteer: undefined,
          volunteerType: undefined,
        }
      };
    },
    fencingnswRules() {
      this.getRules(this.currentNational);
    },
    utasRules() {
      this.getRules(this.currentNational);
    },
    utasData() {
      return {
        ...this.member,
        meta: {
          utasMemberType: 'student',
          utasId: undefined,
        }
      };
    },
    handballRules() {
      this.getRules(this.currentNational);
    },
    handballData() {
      return {
        ...this.member,
        profile: {
          ...this.member.profile,
          meta: {
            ...(this.member.profile.meta || {}),
            parentOrGuardian1: {
              number: undefined,
              email: undefined,
              mobile: undefined,
              phone: undefined,
            },
          }
        }
      };
    },
    defaultData() {
      return {
        ...this.member,
      };
    },
    defaultRules() {
      this.getRules(this.currentNational);
    },
    unisydData() {
      return {
        ...this.member,
        // Custom data for Unisyd
        meta: {
          school: undefined,
          photographyOptOut: false,
          medical: {
            hasMedical: false,
            notes: undefined
          },
          isStaff: false,
          isStudent: false,
          findAboutUs: undefined
        }
      };
    },
    unisydRules() {
      this.getRules(this.currentNational);
    },
    gridironData() {
      return {
        ...this.member,
        meta: {
          nationalId: undefined,
          additionalEmails: undefined,
        }
      };
    },
    gridironRules() {
      this.getRules(this.currentNational);
    },
    archeryData() {
      return {
        ...this.member,
        meta: {
          memberNumber: undefined,
        }
      };
    },
    archeryRules() {
      this.getRules(this.currentNational);
    },
    oztagauData() {
      return {
        ...this.member,
      };
    },
    oztagauRules() {
      this.getRules(this.currentNational);
    },
    bonduniData() {
      return {
        ...this.member,
        meta: {
          bonduniMemberType: 'student',
          bonduniId: undefined,
        }
      };
    },
    bonduniRules() {
      this.getRules(this.currentNational);
    },
    aucklandData() {
      return {
        ...this.member,
        meta: {
          proofOfAge: undefined,
          ethnicity: undefined,
          additionalEthnicities: [], // Array for multi-select
        }
      };
    },

    aucklandRules() {
      this.getRules(this.currentNational);
    },
  },
};
